import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import InputBase from "@material-ui/core/InputBase";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import CircularProgress from "@material-ui/core/CircularProgress";
import Search from "@material-ui/icons/Search";
import FirstPage from "@material-ui/icons/FirstPage";
import LastPage from "@material-ui/icons/LastPage";
import ChevronLeft from "@material-ui/icons/ChevronLeft";
import ChevronRight from "@material-ui/icons/ChevronRight";
import Edit from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

// @material-ui/icons
import CategoryIcon from "@material-ui/icons/Category";

// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Table from "components/Table/Table.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";

import Snackbar from "components/Snackbar/Snackbar.js";
import CheckCircle from "@material-ui/icons/CheckCircle";

import styles from "assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js";

const useStyles = makeStyles(styles);

const GRAPHQL_URL = (process.env.REACT_APP_API_URL || "http://localhost:5001") + "/graphql";

const QUERY = `
  query GetProductCategories($page: Int!, $pageSize: Int!, $search: String) {
    productCategoriesWithPagination(filter: { page: $page, pageSize: $pageSize, search: $search }) {
      data { id name isActive }
      total page pageSize totalPages
    }
  }
`;

const UPDATE_MUTATION = `
  mutation CreateOrUpdateProductCategory($data: CreateProductCategoryDto!) {
    createOrUpdateProductCategory(data: $data)
  }
`;

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function StatusBadge({ active }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 12px",
        borderRadius: "4px",
        fontSize: "12px",
        fontWeight: 600,
        letterSpacing: "0.5px",
        color: "#fff",
        backgroundColor: active ? "#4caf50" : "#9e9e9e",
      }}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function authHeaders() {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
  };
}

export default function ProductCategoriesPage() {
  const classes = useStyles();
  const [categories, setCategories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageInput, setPageInput] = React.useState("1");
  const [search, setSearch] = React.useState("");
  const searchRef = React.useRef("");
  const debounceRef = React.useRef(null);

  // Edit dialog state
  const [editItem, setEditItem] = React.useState(null);
  const [editName, setEditName] = React.useState("");
  const [editActive, setEditActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");
  const [notify, setNotify] = React.useState({ open: false, message: "" });
  const showNotify = (message) => {
    setNotify({ open: true, message });
    setTimeout(() => setNotify((n) => ({ ...n, open: false })), 4000);
  };

  const fetchCategories = React.useCallback((pg, ps, srch) => {
    setLoading(true);
    setError("");
    const token = localStorage.getItem("token");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
      },
      credentials: "include",
      body: JSON.stringify({
        query: QUERY,
        variables: { page: pg, pageSize: ps, search: srch || undefined },
      }),
    })
      .then((res) => res.json())
      .then(({ data, errors }) => {
        if (errors) {
          setError(errors[0].message);
        } else {
          const p = data.productCategoriesWithPagination;
          setCategories(p.data);
          setTotal(p.total);
          setTotalPages(p.totalPages);
          setPage(p.page);
          setPageInput(String(p.page));
        }
      })
      .catch(() => setError("Không thể kết nối máy chủ"))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    fetchCategories(1, pageSize, "");
  }, []);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    searchRef.current = val;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchCategories(1, pageSize, val);
    }, 400);
  };

  const handlePageSizeChange = (e) => {
    const ps = e.target.value;
    setPageSize(ps);
    fetchCategories(1, ps, searchRef.current);
  };

  const goToPage = (pg) => {
    if (pg < 1 || pg > totalPages) return;
    fetchCategories(pg, pageSize, searchRef.current);
  };

  const handlePageInputKey = (e) => {
    if (e.key === "Enter") {
      const pg = parseInt(pageInput, 10);
      if (!isNaN(pg)) goToPage(pg);
    }
  };

  const openAdd = () => {
    setEditItem({ id: null });
    setEditName("");
    setEditActive(true);
    setSaveError("");
  };

  const openEdit = (cat) => {
    setEditItem(cat);
    setEditName(cat.name);
    setEditActive(cat.isActive);
    setSaveError("");
  };

  const closeEdit = () => setEditItem(null);

  const handleSave = () => {
    if (!editName.trim()) { setSaveError("Name is required"); return; }
    setSaving(true);
    setSaveError("");
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: authHeaders(),
      credentials: "include",
      body: JSON.stringify({
        query: UPDATE_MUTATION,
        variables: { data: { id: editItem.id, name: editName.trim(), isActive: editActive } },
      }),
    })
      .then((r) => r.json())
      .then(({ data, errors }) => {
        if (errors) {
          setSaveError(errors[0].message);
        } else if (data.createOrUpdateProductCategory) {
          const isNew = !editItem?.id;
          showNotify(isNew ? "Category created successfully!" : "Category updated successfully!");
          closeEdit();
          fetchCategories(page, pageSize, searchRef.current);
        }
      })
      .catch(() => setSaveError("Không thể kết nối máy chủ"))
      .finally(() => setSaving(false));
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  const tableData = categories.map((c, idx) => [
    <IconButton key={c.id} size="small" onClick={() => openEdit(c)}>
      <Edit fontSize="small" />
    </IconButton>,
    from + idx,
    c.name,
    <StatusBadge key={c.id + "-status"} active={c.isActive} />,
  ]);

  return (
    <>
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color="rose" icon>
            <CardIcon color="rose">
              <CategoryIcon />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>Product Categories</h4>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<AddIcon />}
              onClick={openAdd}
              style={{ position: "absolute", right: "16px", top: "16px" }}
            >
              Add new category
            </Button>
          </CardHeader>
          <CardBody>
            {error && (
              <p style={{ color: "#f44336", textAlign: "center" }}>{error}</p>
            )}

            {/* Search bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px 10px",
                marginBottom: "12px",
                maxWidth: "360px",
              }}
            >
              <Search style={{ color: "#aaa", marginRight: "6px", fontSize: "20px" }} />
              <InputBase
                placeholder="Search by name..."
                value={search}
                onChange={handleSearchChange}
                style={{ flex: 1, fontSize: "14px" }}
              />
            </div>

            <Table
              tableHead={["", "#", "Name", "Status"]}
              tableData={loading ? [] : tableData}
              customCellClasses={[classes.center]}
              customClassesForCells={[0]}
              customHeadCellClasses={[classes.center]}
              customHeadClassesForCells={[0]}
            />
            {loading && (
              <p style={{ textAlign: "center", padding: "16px 0" }}>
                Loading...
              </p>
            )}

            {/* Pagination bar */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: "8px",
                padding: "8px 0",
                flexWrap: "wrap",
                fontSize: "14px",
              }}
            >
              <span>Show</span>
              <Select
                value={pageSize}
                onChange={handlePageSizeChange}
                disableUnderline
                style={{
                  fontSize: "14px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "2px 8px",
                }}
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt} rows
                  </MenuItem>
                ))}
              </Select>

              <IconButton size="small" onClick={() => goToPage(1)} disabled={page <= 1}>
                <FirstPage fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => goToPage(page - 1)} disabled={page <= 1}>
                <ChevronLeft fontSize="small" />
              </IconButton>

              <input
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onKeyDown={handlePageInputKey}
                onBlur={() => {
                  const pg = parseInt(pageInput, 10);
                  if (!isNaN(pg)) goToPage(pg);
                  else setPageInput(String(page));
                }}
                style={{
                  width: "48px",
                  textAlign: "center",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "4px 0",
                  fontSize: "14px",
                }}
              />

              <IconButton size="small" onClick={() => goToPage(page + 1)} disabled={page >= totalPages}>
                <ChevronRight fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => goToPage(totalPages)} disabled={page >= totalPages}>
                <LastPage fontSize="small" />
              </IconButton>

              <span style={{ color: "#555" }}>
                {from} – {to} of {total.toLocaleString("vi-VN")} categories
              </span>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>

    {/* Edit Dialog */}
    <Dialog open={!!editItem} onClose={closeEdit} maxWidth="xs" fullWidth>
      <DialogTitle>{editItem?.id ? "Edit Category" : "Add Category"}</DialogTitle>
      <DialogContent>
        {saveError && (
          <p style={{ color: "#f44336", margin: "0 0 12px" }}>{saveError}</p>
        )}
        <TextField
          label="Name"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          style={{ marginBottom: "16px", marginTop: "4px" }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={editActive}
              onChange={(e) => setEditActive(e.target.checked)}
              color="primary"
            />
          }
          label={editActive ? "Active" : "Inactive"}
        />
      </DialogContent>
      <DialogActions style={{ padding: "8px 24px 16px" }}>
        <Button onClick={closeEdit} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
    <Snackbar
      place="tr"
      color="success"
      icon={CheckCircle}
      message={notify.message}
      open={notify.open}
      closeNotification={() => setNotify((n) => ({ ...n, open: false }))}
      close
    />
    </>
  );
}
